import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const getVisitorId = (): string => {
  const key = "visitor_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

/** Cached admin status to avoid repeated RPC calls. */
let adminStatusCache: boolean | null = null;
let adminCheckPromise: Promise<boolean> | null = null;

const checkIsAdmin = (): Promise<boolean> => {
  if (adminStatusCache !== null) return Promise.resolve(adminStatusCache);
  if (adminCheckPromise) return adminCheckPromise;
  adminCheckPromise = (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { adminStatusCache = false; return false; }
      const { data } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });
      adminStatusCache = data === true;
      return adminStatusCache;
    } catch {
      adminStatusCache = false;
      return false;
    }
  })();
  return adminCheckPromise;
};

// Reset cache on auth state change
supabase.auth.onAuthStateChange(() => {
  adminStatusCache = null;
  adminCheckPromise = null;
});

const isLovablePreview = (): boolean => {
  const host = window.location.hostname;
  return host.includes("lovableproject.com") || host.includes("lovable.dev");
};

const insertEvent = (eventName: string, eventData?: Record<string, unknown>) => {
  if (isLovablePreview()) return; // Never track from Lovable preview

  const visitorId = getVisitorId();
  checkIsAdmin().then((isAdmin) => {
    if (isAdmin) return;
    (supabase.from("analytics_events") as any)
      .insert({
        visitor_id: visitorId,
        event_name: eventName,
        event_data: eventData || {},
        path: window.location.pathname,
      })
      .then(({ error }: { error: any }) => {
        if (error) console.error("Event tracking error:", error);
      });
  });
};

export const useEventTracking = () => {
  const trackEvent = useCallback((eventName: string, eventData?: Record<string, unknown>) => {
    insertEvent(eventName, eventData);
  }, []);

  return { trackEvent };
};

// Standalone function for use outside React components
export const trackEvent = (eventName: string, eventData?: Record<string, unknown>) => {
  insertEvent(eventName, eventData);
};
